import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, LifeBuoy } from "lucide-react";
import { OneTimeCredentials } from "@/components/OneTimeCredentials";

const digits = (v) => v.replace(/\D/g, "").slice(0, 6);

// Owner self-service PIN reset (password + 2FA) and emergency recovery (temporary PIN -> new PIN).
export default function OwnerPinResetDialog({ open, onClose, onDone, mode: initialMode = "reset" }) {
  const [mode, setMode] = useState(initialMode);
  const [sec, setSec] = useState(null);
  const [f, setF] = useState({ password: "", code: "", temp_pin: "", new_pin: "", confirm_pin: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode(initialMode); setErr(""); setF({ password: "", code: "", temp_pin: "", new_pin: "", confirm_pin: "" });
    api.get("/auth/security").then((r) => { setSec(r.data); if (r.data.pin_recovery_pending && initialMode === "reset") setMode("recover"); }).catch(() => {});
  }, [open, initialMode]);

  const [otc, setOtc] = useState(null);

  const submit = async (generate = false) => {
    setErr("");
    if (!generate) {
      if (f.new_pin.length !== 6) { setErr("Authorization PIN must be exactly 6 digits"); return; }
      if (f.new_pin !== f.confirm_pin) { setErr("PINs do not match"); return; }
    }
    setBusy(true);
    try {
      let data;
      if (mode === "recover") ({ data } = await api.post("/auth/pin/recover", { temp_pin: f.temp_pin, new_pin: f.new_pin, confirm_pin: f.confirm_pin }));
      else ({ data } = await api.post("/auth/pin/reset", generate ? { password: f.password, code: f.code || null, generate: true } : { password: f.password, code: f.code || null, new_pin: f.new_pin, confirm_pin: f.confirm_pin }));
      toast.success(mode === "recover" ? "Recovery complete. New PIN established" : "Owner Authorization PIN updated");
      onDone?.(); onClose();
      if (generate && data?.issued_credentials) setOtc(data.issued_credentials);
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const recover = mode === "recover";
  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="owner-pin-reset-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-violet-500/15 text-violet-500">{recover ? <LifeBuoy className="w-5 h-5" /> : <KeyRound className="w-5 h-5" />}</span>
            {recover ? "Complete PIN Recovery" : sec?.has_pin ? "Reset Owner PIN" : "Set Owner PIN"}
          </DialogTitle>
          <DialogDescription>
            {recover ? "Enter the temporary recovery PIN issued by NEEC Support, then create your new permanent PIN. The temporary PIN is single-use and expires."
              : `Verify your identity with your login password${sec?.mfa_enabled ? " and authenticator code" : ""}, then choose a new 6-digit Authorization PIN (or let NEEC generate one). It must differ from every other PIN in your business. Your current PIN is never shown and stops working immediately.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {recover ? (
            <div><Label className="text-xs">Temporary recovery PIN</Label><Input value={f.temp_pin} onChange={(e) => setF({ ...f, temp_pin: digits(e.target.value) })} inputMode="numeric" className="mono text-lg tracking-widest" autoFocus data-testid="pin-temp" /></div>
          ) : (<>
            <div><Label className="text-xs">Login password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoFocus data-testid="pin-reset-password" /></div>
            {sec?.mfa_enabled && <div><Label className="text-xs">Authenticator code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.trim() })} inputMode="numeric" className="mono" data-testid="pin-reset-code" /></div>}
          </>)}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">New 6-digit Authorization PIN</Label><Input type="password" value={f.new_pin} onChange={(e) => setF({ ...f, new_pin: digits(e.target.value) })} inputMode="numeric" className="mono text-lg" data-testid="pin-new" /></div>
            <div><Label className="text-xs">Confirm new PIN</Label><Input type="password" value={f.confirm_pin} onChange={(e) => setF({ ...f, confirm_pin: digits(e.target.value) })} inputMode="numeric" className="mono text-lg" data-testid="pin-confirm" /></div>
          </div>
          {err && <p className="text-sm text-rose-500" data-testid="pin-reset-error">{err}</p>}
          <Button className="w-full h-11 bg-violet-600 hover:bg-violet-700 text-white" disabled={busy} onClick={() => submit(false)} data-testid="pin-reset-submit">
            {busy ? "Saving…" : recover ? "Establish New PIN" : "Save New PIN"}
          </Button>
          {!recover && <Button variant="outline" className="w-full" disabled={busy} onClick={() => submit(true)} data-testid="pin-reset-generate">Generate a 6-digit PIN for me (shown once)</Button>}
          {sec?.pin_recovery_pending && !recover && (
            <button type="button" className="text-xs text-violet-500 hover:underline w-full text-center" onClick={() => setMode("recover")} data-testid="pin-switch-recover">I have a temporary recovery PIN from NEEC Support</button>
          )}
          {recover && (
            <button type="button" className="text-xs text-muted-foreground hover:underline w-full text-center" onClick={() => setMode("reset")} data-testid="pin-switch-reset">Use my password instead</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    <OneTimeCredentials creds={otc} onClose={() => setOtc(null)} />
    </>
  );
}
